import { Overlay } from '@angular/cdk/overlay';
import type { OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, viewChild, ViewContainerRef } from '@angular/core';
import type { AfterViewInit, TemplateRef } from '@angular/core';
import type { StmToast, StmToastTone } from './toast.service';
import { HERO_ICONS } from '../../hero-icons';
import { STM_TOAST_EXTENDED_DURATION_MS, StmToastService } from './toast.service';

type PauseReason = 'focus' | 'pointer';

type ToastTimer = {
    handle: ReturnType<typeof setTimeout> | null;
    readonly pauseReasons: Set<PauseReason>;
};

type ToastPresentation = {
    readonly accent: string;
    readonly body: string;
    readonly icon: string;
    readonly iconTile: string;
};

const TOAST_PRESENTATION: Record<StmToastTone, ToastPresentation> = {
    success: {
        accent: 'bg-emerald-500',
        body: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        icon: HERO_ICONS.checkCircle,
        iconTile: 'bg-white/60 text-emerald-700'
    },
    error: {
        accent: 'bg-rose-500',
        body: 'border-rose-200 bg-rose-50 text-rose-900',
        icon: HERO_ICONS.xCircle,
        iconTile: 'bg-white/60 text-rose-700'
    },
    info: {
        accent: 'bg-blue-500',
        body: 'border-blue-200 bg-blue-50 text-blue-900',
        icon: HERO_ICONS.info,
        iconTile: 'bg-white/60 text-blue-700'
    }
};

@Component({
    selector: 'stm-toast-outlet',
    template: `
        <ng-template #toastContent>
            <section data-testid="toast-outlet" class="pointer-events-none flex w-full flex-col gap-2" aria-label="Notifications">
                @for (toast of toastService.toasts(); track toast.id) {
                    <article
                        data-testid="toast"
                        class="pointer-events-auto relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-[8px] border p-3 pr-1 pl-4 shadow-lg"
                        [class]="presentation[toast.tone].body"
                        [attr.data-toast-id]="toast.id"
                        [attr.data-toast-tone]="toast.tone"
                        [attr.role]="toast.tone === 'error' ? 'alert' : 'status'"
                        aria-atomic="true"
                        (mouseenter)="pause(toast.id, 'pointer')"
                        (mouseleave)="resume(toast.id, 'pointer')"
                        (focusin)="pause(toast.id, 'focus')"
                        (focusout)="resumeAfterFocus(toast.id, $event)"
                        (click)="dismiss(toast.id)"
                    >
                        <span class="absolute inset-y-0 left-0 w-1" [class]="presentation[toast.tone].accent" aria-hidden="true"></span>
                        <span class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[8px]" [class]="presentation[toast.tone].iconTile" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="size-5">
                                <path [attr.d]="presentation[toast.tone].icon" />
                            </svg>
                        </span>
                        <span class="min-w-0 flex-1 py-1">
                            @if (toast.title) {
                                <strong class="block text-sm font-semibold">{{ toast.title }}</strong>
                            }
                            <span class="block text-sm leading-5 break-words opacity-80">{{ toast.message }}</span>
                        </span>
                        <button
                            type="button"
                            class="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-current opacity-70 hover:bg-black/5 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                            [attr.aria-label]="'Dismiss ' + toast.tone + ' notification'"
                            (click)="dismissFromButton(toast.id, $event)"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                                <path [attr.d]="icons.xmark" />
                            </svg>
                        </button>
                    </article>
                }
            </section>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StmToastOutletComponent implements AfterViewInit {
    readonly toastService = inject(StmToastService);
    readonly icons = HERO_ICONS;
    readonly presentation = TOAST_PRESENTATION;

    private readonly destroyRef = inject(DestroyRef);
    private readonly overlay = inject(Overlay);
    private readonly toastContent = viewChild.required<TemplateRef<unknown>>('toastContent');
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly timers = new Map<number, ToastTimer>();
    private overlayRef: OverlayRef | null = null;

    private readonly timerEffect = effect(() => {
        const toasts = this.toastService.toasts();
        const activeIds = new Set(toasts.map((toast) => toast.id));

        for (const [id, timer] of this.timers) {
            if (!activeIds.has(id)) {
                this.clearTimer(timer);
                this.timers.delete(id);
            }
        }

        for (const toast of toasts) {
            if (!this.timers.has(toast.id)) {
                const timer: ToastTimer = { handle: null, pauseReasons: new Set<PauseReason>() };
                this.timers.set(toast.id, timer);
                this.schedule(toast, toast.durationMs);
            }
        }
    });

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.timerEffect.destroy();
            for (const timer of this.timers.values()) {
                this.clearTimer(timer);
            }
            this.timers.clear();
            this.overlayRef?.dispose();
        });
    }

    ngAfterViewInit(): void {
        const overlayRef = this.overlay.create({
            maxWidth: '24rem',
            panelClass: 'pointer-events-none',
            positionStrategy: this.overlay.position().global().top('0.75rem').right('0.75rem'),
            usePopover: true,
            width: 'calc(100vw - 1.5rem)'
        });
        overlayRef.attach(new TemplatePortal(this.toastContent(), this.viewContainerRef));
        this.overlayRef = overlayRef;
    }

    dismiss(id: number): void {
        const timer = this.timers.get(id);
        if (timer) {
            this.clearTimer(timer);
            this.timers.delete(id);
        }

        this.toastService.dismiss(id);
    }

    dismissFromButton(id: number, event: MouseEvent): void {
        event.stopPropagation();
        this.dismiss(id);
    }

    pause(id: number, reason: PauseReason): void {
        const timer = this.timers.get(id);
        if (!timer) {
            return;
        }

        timer.pauseReasons.add(reason);
        this.clearTimer(timer);
    }

    resume(id: number, reason: PauseReason): void {
        const timer = this.timers.get(id);
        const toast = this.findToast(id);
        if (!timer || !toast) {
            return;
        }

        timer.pauseReasons.delete(reason);
        if (timer.pauseReasons.size === 0 && toast.durationMs !== null) {
            this.schedule(toast, STM_TOAST_EXTENDED_DURATION_MS);
        }
    }

    resumeAfterFocus(id: number, event: FocusEvent): void {
        const currentTarget = event.currentTarget;
        const nextTarget = event.relatedTarget;
        if (currentTarget instanceof Node && nextTarget instanceof Node && currentTarget.contains(nextTarget)) {
            return;
        }

        this.resume(id, 'focus');
    }

    private findToast(id: number): StmToast | undefined {
        return this.toastService.toasts().find((toast) => toast.id === id);
    }

    private schedule(toast: StmToast, durationMs: number | null): void {
        const timer = this.timers.get(toast.id);
        if (!timer || durationMs === null || timer.pauseReasons.size > 0) {
            return;
        }

        this.clearTimer(timer);
        timer.handle = setTimeout(() => {
            timer.handle = null;
            this.toastService.dismiss(toast.id);
        }, durationMs);
    }

    private clearTimer(timer: ToastTimer): void {
        if (timer.handle !== null) {
            clearTimeout(timer.handle);
            timer.handle = null;
        }
    }
}
